import { useApi } from "@/shared/api";
import type {
  AddMessageToTicket,
  ChangeTicketStatus,
  GetModeratorTicketsRequest,
  GetTicketsRequest,
} from "./api.dto";
import { filterObjectKeys } from "@/shared/lib/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMutate } from "@/shared/hooks";
import type { AxiosError, AxiosResponse } from "axios";
import type { ModeratorTicketTable } from "../model";
import type { PaginatedResponse } from "@/shared/types";

export const useModeratorTickets = (props: GetModeratorTicketsRequest) => {
  const { api } = useApi();
  const params = filterObjectKeys(props);

  return useQuery<PaginatedResponse<ModeratorTicketTable>>({
    queryKey: ["moderatorLeads", params],
    enabled: params.moderatorId !== "0",
    queryFn: async () => {
      try {
        const response = await api.get(
          `/tickets/moderator/${params.moderatorId}`,
          {
            params: filterObjectKeys(params, ["moderatorId"]),
          },
        );
        return response.data;
      } catch (e) {
        console.error(`${e}`);
      }
    },
  });
};

export const useTickets = (props: GetTicketsRequest) => {
  const { api } = useApi();
  const params = filterObjectKeys(props);

  return useQuery<PaginatedResponse<ModeratorTicketTable>>({
    queryKey: ["allTickets", params],
    queryFn: async () => {
      try {
        const response = await api.get(`/tickets/`, {
          params,
        });
        return response.data;
      } catch (e) {
        console.error(`${e}`);
      }
    },
  });
};

export const useAddMessageToTicket = ({ id }: { id: string }) => {
  const { mutationFn, onError, onSuccess } = useMutate({
    method: "post",
    url: `/tickets/${id}/message`,
    toast: {
      success: {
        description: "Сообщение отправлено.",
      },
    },
    refetchQueries: ["moderatorLeads"],
  });

  return useMutation<AxiosResponse, AxiosError, AddMessageToTicket, Response>({
    mutationKey: ["addMessageToTicket"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const useChangeTicketStatus = ({ id }: { id: string }) => {
  const { mutationFn, onError, onSuccess } = useMutate({
    method: "patch",
    url: `/tickets/${id}/status`,
    toast: {
      success: {
        description: "Статус тикета изменён.",
      },
    },
    refetchQueries: ["moderatorLeads"],
  });

  return useMutation<AxiosResponse, AxiosError, ChangeTicketStatus, Response>({
    mutationKey: ["changeTicketStatus"],
    mutationFn,
    onSuccess,
    onError,
  });
};
